import React, { useMemo } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import type { RedesignThemeColors } from '@/constants/residents/theme';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;
const ClockIcon = RESIDENTS_SVG_COMPONENTS.CLOCK;

interface TransactionHistorySectionProps {
  transactions: HistoryRow[];
  maxItems?: number;
  onViewAll?: () => void;
  onTransactionPress?: (transaction: HistoryRow) => void;
  style?: StyleProp<ViewStyle>;
}

type TransactionStatus = 'received' | 'processing' | 'declined' | 'charge';

const getTransactionStatus = (row: HistoryRow): TransactionStatus => {
  if (row.type === 'payment') {
    if (row.status === 'pending') return 'processing';
    if (row.status === 'declined') return 'declined';
    return 'received';
  }
  return 'charge';
};

const getStatusLabel = (row: HistoryRow): string => {
  const status = getTransactionStatus(row);
  switch (status) {
    case 'processing': return 'Payment processing';
    case 'received': return 'Payment received';
    case 'declined': return 'Payment declined';
    default: return row.description;
  }
};

const getStatusIconConfig = (status: TransactionStatus, dimmerColor: string) => {
  const configs: Record<TransactionStatus, { bg: string; border: string; color: string }> = {
    received: {
      bg: 'rgba(102,206,102,0.08)',
      border: 'rgba(102,206,102,0.08)',
      color: '#66ce66',
    },
    processing: {
      bg: 'rgba(255,118,41,0.08)',
      border: 'rgba(255,118,41,0.08)',
      color: '#ff7629',
    },
    declined: {
      bg: 'rgba(245,85,96,0.08)',
      border: 'rgba(245,85,96,0.08)',
      color: '#f55560',
    },
    charge: {
      bg: 'rgba(255,255,255,0.05)',
      border: 'rgba(255,255,255,0.04)',
      color: dimmerColor,
    },
  };
  return configs[status];
};

const TransactionIcon: React.FC<{ status: TransactionStatus; dimmerColor: string }> = React.memo(({ status, dimmerColor }) => {
  const config = getStatusIconConfig(status, dimmerColor);
  return (
    <View style={[styles.statusIcon, { backgroundColor: config.bg, borderColor: config.border }]}>
      {status === 'processing' && ClockIcon ? (
        <ClockIcon width={18} height={18} color={config.color} />
      ) : CheckIcon ? (
        <CheckIcon width={18} height={18} color={config.color} />
      ) : null}
    </View>
  );
});

const TransactionRow: React.FC<{
  transaction: HistoryRow;
  isLast: boolean;
  onPress?: (transaction: HistoryRow) => void;
  themeColors: RedesignThemeColors;
}> = React.memo(({ transaction, isLast, onPress, themeColors }) => {
  const status = getTransactionStatus(transaction);
  const label = getStatusLabel(transaction);
  const amount = formatCurrency(Math.abs(transaction.amount));
  const displayAmount = transaction.amount < 0 || transaction.type === 'payment' ? `-${amount}` : amount;

  const rowStyle = [styles.row, !isLast && [styles.rowBorder, { borderBottomColor: themeColors.separator }]];
  const content = (
    <>
      <TransactionIcon status={status} dimmerColor={themeColors.text.dimmer} />

      <View style={styles.rowContent}>
        <Text style={[styles.rowTitle, { color: themeColors.text.tertiary }]} numberOfLines={2}>{label}</Text>
        <Text style={[styles.rowDate, { color: themeColors.text.dim }]}>{formatShortDate(transaction.date)}</Text>
      </View>

      <View style={styles.rowAmountContainer}>
        <Text style={[styles.rowAmount, { color: themeColors.text.primary }]}>{displayAmount}</Text>
        {transaction.paymentMethod && (
          <Text style={[styles.rowPaymentMethod, { color: themeColors.text.dimmer }]}>{transaction.paymentMethod}</Text>
        )}
      </View>
    </>
  );

  return onPress ? (
    <Hoverable hover="row" style={rowStyle} onPress={() => onPress(transaction)}>
      {content}
    </Hoverable>
  ) : (
    <View style={rowStyle}>{content}</View>
  );
});

export const TransactionHistorySection: React.FC<TransactionHistorySectionProps> = React.memo(({
  transactions,
  maxItems,
  onViewAll,
  onTransactionPress,
  style,
}) => {
  const { colors, semantic } = useThemeColors();

  const displayTransactions = maxItems ? transactions.slice(0, maxItems) : transactions;

  const headerRow = (
    <View style={styles.cardHeader}>
      <Text style={[styles.cardHeaderTitle, { color: colors.text.secondary }]}>Transaction History</Text>
      {onViewAll && (
        <Hoverable hover="link" onPress={onViewAll} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          {({ textStyle }) => (
            <Text style={[styles.cardHeaderAction, { color: semantic.interactive.primary }, textStyle]}>View all</Text>
          )}
        </Hoverable>
      )}
    </View>
  );

  if (transactions.length === 0) {
    return (
      <GlassCard padding={0} borderRadius={16} style={style}>
        {headerRow}
        <Text style={[styles.emptyText, { color: colors.text.muted }]}>No transaction history available yet.</Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={0} borderRadius={16} style={style}>
      {headerRow}
      {displayTransactions.map((transaction, index) => (
        <TransactionRow
          key={transaction.id}
          transaction={transaction}
          isLast={index === displayTransactions.length - 1}
          onPress={onTransactionPress}
          themeColors={colors}
        />
      ))}
    </GlassCard>
  );
});

TransactionHistorySection.displayName = 'TransactionHistorySection';

const styles = StyleSheet.create({
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
  },
  cardHeaderTitle: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  cardHeaderAction: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  statusIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  rowBorder: {
    borderBottomWidth: 1,
  },
  rowContent: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  rowDate: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  rowAmountContainer: {
    alignItems: 'flex-end',
    gap: 2,
  },
  rowAmount: {
    fontSize: 14,
    fontWeight: '600',
  },
  rowPaymentMethod: {
    fontSize: 11,
    fontWeight: '400',
    textAlign: 'right',
  },
  emptyText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
    padding: 20,
  },
});
